import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from '../../components/Common/Card';
import Table from '../../components/Common/Table';
import Button from '../../components/Common/Button';
import api from '../../utils/api';
import { Plus } from 'lucide-react';
import { formatStatus } from '../../utils/formatters';

const MeetingFilteredList = ({ title, filterType }) => {
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchMeetings();
  }, [filterType]);

  const fetchMeetings = async () => {
    try {
      const response = await api.get('/meetings');
      let allMeetings = response.data.data || response.data;

      // Filter meetings based on filterType
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);

      let filteredMeetings = allMeetings;

      if (filterType === 'today') {
        // Bugünkü görüşmeler
        filteredMeetings = allMeetings.filter(meeting => {
          const meetingDate = new Date(meeting.date);
          meetingDate.setHours(0, 0, 0, 0);
          return meetingDate.getTime() === today.getTime();
        });
      } else if (filterType === 'future') {
        // Gelecek görüşmeler (yarından sonraki)
        filteredMeetings = allMeetings.filter(meeting => {
          const meetingDate = new Date(meeting.date);
          meetingDate.setHours(0, 0, 0, 0);
          return meetingDate.getTime() >= tomorrow.getTime();
        });
      } else if (filterType === 'past') {
        // Geçmiş görüşmeler (dünden önceki)
        filteredMeetings = allMeetings.filter(meeting => {
          const meetingDate = new Date(meeting.date);
          meetingDate.setHours(0, 0, 0, 0);
          return meetingDate.getTime() < today.getTime();
        });
      }

      setMeetings(filteredMeetings);
      setLoading(false);
    } catch (error) {
      console.error('Görüşmeler yüklenemedi:', error);
      setLoading(false);
    }
  };

  const handleDelete = async (row) => {
    const customerName = `${row.customer?.name} ${row.customer?.surname}`;
    if (!window.confirm(`${customerName} ile yapılan görüşmeyi silmek istediğinizden emin misiniz?`)) {
      return;
    }

    try {
      await api.delete(`/meetings/${row.id}`);
      fetchMeetings();
    } catch (error) {
      console.error('Görüşme silinemedi:', error);
      alert('Görüşme silinirken bir hata oluştu');
    }
  };

  const columns = [
    {
      header: 'Tarih',
      accessor: 'date',
      render: (row) => new Date(row.date).toLocaleDateString('tr-TR')
    },
    {
      header: 'Müşteri',
      render: (row) => (
        <span
          className="text-gray-900 dark:text-gray-100 hover:text-blue-600 dark:hover:text-blue-400 cursor-pointer font-medium"
          onClick={(e) => {
            e.stopPropagation();
            if (row.customer?.id) navigate(`/customers/${row.customer.id}`);
          }}
        >
          {row.customer?.name} {row.customer?.surname}
        </span>
      )
    },
    {
      header: 'Temsilci',
      accessor: 'user',
      render: (row) => row.user?.name
    },
    { header: 'Tip', accessor: 'type', render: (row) => formatStatus(row.type) },
    {
      header: 'Durum',
      accessor: 'is_completed',
      render: (row) => (
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${row.is_completed
            ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
            : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
          }`}>
          {row.is_completed ? 'Tamamlandı' : 'Bekliyor'}
        </span>
      ),
    },
  ];

  if (loading) {
    return <div className="text-center py-8">Yükleniyor...</div>;
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800 dark:text-white">{title}</h1>
        <Button onClick={() => navigate('/meetings/new')}>
          <Plus size={18} />
          Yeni Görüşme
        </Button>
      </div>

      <Card noPadding>
        <Table
          columns={columns}
          data={meetings}
          onView={(row) => navigate(`/meetings/${row.id}`)}
          onEdit={(row) => navigate(`/meetings/${row.id}/edit`)}
          onDelete={handleDelete}
          customActions={(row) => (
            !row.is_completed && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/meetings/${row.id}/complete`);
                }}
                className="p-2 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-all hover:scale-110"
                title="Tamamla"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                  <polyline points="22 4 12 14.01 9 11.01"></polyline>
                </svg>
              </button>
            )
          )}
        />
      </Card>
    </div>
  );
};

export default MeetingFilteredList;
